// src/features/reits-ranking/ReitsRankingPage.tsx
//
// REITs (Real-Estate equities) ranking feature page. Sibling of
// features/stock-ranking/StockRankingPage and
// features/etf-ranking/EtfRankingPage. Loads:
//   - the universe-wide factor aggregates from
//     GET /api/v1/reits-ranking/sector-summary once (drives the factor
//     summary panel), and
//   - the ranked cross-section from GET /api/v1/reits-ranking/universe.
// Unlike the ETF page there are NO category pills — the REIT universe is a
// single "Real Estate" sector — so the universe is fetched once and sorted
// entirely client-side. A single batched call to
// GET /api/v1/reits-ranking/historical-batch powers the table sparklines
// for the currently-visible rows (no N+1 waterfall). Reuses the standard
// <Layout sidebar={<Sidebar />}> dashboard shell so it sits behind the same
// auth gate / navigation as the rest of the suite.

import { useEffect, useMemo, useState } from 'react';

import { GlobalSearchBar } from '../global-search';
import { Layout, Sidebar } from '../../shared/ui';
import {
    fetchHistoricalBatch,
    fetchSectorSummary,
    fetchUniverse,
    type HistoricalBatchResponse,
    type SectorSummaryRow,
    type UniverseRow,
} from './api';
import { FactorSummary } from './components/FactorSummary';
import { ReitsDetailPopup } from './components/ReitsDetailPopup';
import { RiskReturnScatter } from './components/RiskReturnScatter';
import { UniverseTable, type SortKey } from './components/UniverseTable';

// Cap the batched sparkline fetch to the top N rows of the current sort.
// This bounds the DB window query + payload regardless of how the user
// sorts, while still giving the most-relevant rows a trend.
const SPARKLINE_LIMIT = 60;

export const ReitsRankingPage = () => {
    const [universe, setUniverse] = useState<UniverseRow[]>([]);
    const [sectorSummary, setSectorSummary] = useState<SectorSummaryRow[]>([]);
    const [asOfDate, setAsOfDate] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [sortKey, setSortKey] = useState<SortKey>('universe_rank');
    const [sortDesc, setSortDesc] = useState<boolean>(false);
    const [hoverSymbol, setHoverSymbol] = useState<string | null>(null);
    // Ticker clicked in the table — drives the quick-stats pop-over.
    const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);

    const [sparklines, setSparklines] = useState<HistoricalBatchResponse>({});

    // --- Sector aggregates: loaded ONCE on mount. ----------------------
    // Drives the factor-summary panel. Non-fatal on failure: the universe
    // effect owns the page-level error state, so a missing panel should
    // not blank the whole page.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchSectorSummary();
                if (!cancelled) setSectorSummary(res.sectors);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('Failed to load sector summary', err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // --- Universe: loaded ONCE on mount (single sector, no filter). ----
    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        (async () => {
            try {
                const res = await fetchUniverse();
                if (cancelled) return;
                setUniverse(res.items);
                setAsOfDate(res.as_of_date);
                setError(null);
            } catch (err) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('Failed to load REIT ranking universe', err);
                setError(
                    'Unable to load REIT rankings right now. Please try again shortly.',
                );
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // --- Client-side SORT. ---------------------------------------------
    const processed = useMemo(() => {
        const sorted = [...universe].sort((a, b) => {
            const va = a[sortKey];
            const vb = b[sortKey];
            let cmp: number;
            if (typeof va === 'string' && typeof vb === 'string') {
                cmp = va.localeCompare(vb);
            } else {
                cmp = (va as number) - (vb as number);
            }
            return sortDesc ? -cmp : cmp;
        });
        return sorted;
    }, [universe, sortKey, sortDesc]);

    // --- Symbols whose sparklines we batch-fetch (top of current view). -
    const sparklineSymbols = useMemo(
        () => processed.slice(0, SPARKLINE_LIMIT).map((r) => r.symbol),
        [processed],
    );
    const sparklineSymbolSet = useMemo(
        () => new Set(sparklineSymbols),
        [sparklineSymbols],
    );

    // --- Batched sparkline fetch, refreshed when the visible set changes.
    useEffect(() => {
        if (sparklineSymbols.length === 0) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchHistoricalBatch(sparklineSymbols, 90);
                if (cancelled) return;
                // Merge so previously-loaded symbols persist when the
                // visible window shifts (avoids a flash of empty trends).
                setSparklines((prev) => ({ ...prev, ...res }));
            } catch (err) {
                // Sparklines are non-critical; never surface as a page error.
                // eslint-disable-next-line no-console
                console.warn('Failed to load sparkline history', err);
            }
        })();
        return () => {
            cancelled = true;
        };
        // Join key keeps the effect stable when the array identity changes
        // but the contents don't.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sparklineSymbols.join(',')]);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDesc((d) => !d);
        } else {
            setSortKey(key);
            // Rank/symbol/sector read best ascending; factor columns best
            // descending (strongest first).
            setSortDesc(
                key !== 'universe_rank' &&
                    key !== 'symbol' &&
                    key !== 'sector',
            );
        }
    };

    return (
        <Layout
            sidebar={<Sidebar />}
            title="REITs Rankings"
            subtitle="Cross-sectional multi-factor scores for the Real-Estate Investment Trust universe."
        >
            {/* Platform-wide asset search — jump to any deep-dive page. */}
            <div className="mb-8">
                <GlobalSearchBar />
            </div>

            {/* Header row: freshness badge. */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <p className="muted text-sm">
                    {processed.length > 0
                        ? `${processed.length} REITs ranked`
                        : 'Real Estate Investment Trusts'}
                </p>
                {asOfDate && (
                    <span className="text-xs font-mono text-slate-500 bg-slate-100 dark:bg-slate-800/50 px-2 py-1 rounded">
                        Updated: {asOfDate}
                    </span>
                )}
            </div>

            {error && (
                <div className="mb-6 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="h-64 flex items-center justify-center text-slate-500 dark:text-slate-400">
                    Loading quantitative data…
                </div>
            ) : (
                <>
                    {/* Charts: cross-sectional Risk/Return + factor summary. */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
                        <div className="surface-card">
                            <h2 className="section-title mb-1">
                                Risk vs Return
                            </h2>
                            <p className="muted text-xs mb-4">
                                {processed.length} REITs · Forge Ultra Signal
                                vs annualized volatility
                            </p>
                            <RiskReturnScatter
                                data={processed}
                                highlightSymbol={hoverSymbol}
                            />
                        </div>
                        <div className="surface-card">
                            <h2 className="section-title mb-1">
                                Factor Strength
                            </h2>
                            <p className="muted text-xs mb-4">
                                Universe-wide average z-scores
                            </p>
                            <FactorSummary sectors={sectorSummary} />
                        </div>
                    </div>

                    {/* Full sortable universe table. */}
                    <UniverseTable
                        data={processed}
                        sparklines={sparklines}
                        sparklineSymbols={sparklineSymbolSet}
                        sortKey={sortKey}
                        sortDesc={sortDesc}
                        onSort={handleSort}
                        onRowHover={setHoverSymbol}
                        onSymbolClick={setSelectedSymbol}
                    />
                </>
            )}

            {/* Tabbed detail pop-over for the clicked ticker (price/volume,
                signal, factor charts + the quick-overview header stats). */}
            {selectedSymbol && (
                <ReitsDetailPopup
                    symbol={selectedSymbol}
                    onClose={() => setSelectedSymbol(null)}
                />
            )}
        </Layout>
    );
};

export default ReitsRankingPage;
